// src/components/quiz/SurvivalMode.js
// Modalità "Sopravvivenza": rispondi finché non sbagli o scade il tempo.
// Meccanica invariata rispetto alla versione originale del quiz — solo
// estratta in un componente dedicato per il redesign multi-modalità.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trophy, Clock, CheckCircle2, XCircle, Timer } from 'lucide-react';
import { fmtTime, OptionBtn, LETTERS } from './quizShared';
import { pickNextQuestion } from './quizGenerators';

const SECONDS_PER_Q = 15;
const FEEDBACK_DELAY_OK  = 1200;  // ms dopo risposta corretta
const FEEDBACK_DELAY_ERR = 2200;  // ms dopo risposta errata

// La difficoltà sale ogni DIFFICULTY_STEP risposte corrette consecutive:
// meno tempo a domanda, più punti per le risposte giuste.
const DIFFICULTY_STEP = 10;
const MIN_SECONDS_PER_Q = 6;
const TIME_STEP_PER_LEVEL = 2;
const SCORE_BONUS_PER_LEVEL = 0.3;

function getLevelForIndex(idx) {
  return Math.floor(idx / DIFFICULTY_STEP);
}
function getSecondsForLevel(level) {
  return Math.max(MIN_SECONDS_PER_Q, SECONDS_PER_Q - level * TIME_STEP_PER_LEVEL);
}
function getScoreBonusForLevel(level) {
  return 1 + level * SCORE_BONUS_PER_LEVEL;
}

export default function SurvivalMode({ players, onFinish }) {
  const [idx,         setIdx]         = useState(0);
  const [q,           setQ]           = useState(() => pickNextQuestion(players));
  const [timeLeft,    setTimeLeft]    = useState(SECONDS_PER_Q);
  const [totalSecs,   setTotalSecs]   = useState(0);
  const [selected,    setSelected]    = useState(null); // option index or 'timeout'
  const [breakdown,   setBreakdown]   = useState([]);
  const [totalScore,  setTotalScore]  = useState(0);

  const timerRef       = useRef(null);
  const totalRef       = useRef(null);
  const recentTextsRef = useRef([]);

  const level        = getLevelForIndex(idx);
  const secondsForQ   = getSecondsForLevel(level);
  const scoreBonus    = getScoreBonusForLevel(level);

  // Total elapsed timer
  useEffect(() => {
    totalRef.current = setInterval(() => setTotalSecs(s => s + 1), 1000);
    return () => clearInterval(totalRef.current);
  }, []);

  // Per-question countdown (la durata dipende dal livello di difficoltà)
  const startQuestionTimer = useCallback((seconds) => {
    setTimeLeft(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          // Timeout → treat as wrong
          setSelected('timeout');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    setSelected(null);
    startQuestionTimer(secondsForQ);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, startQuestionTimer]);

  const handleSelect = useCallback((optIdx) => {
    if (selected !== null) return;
    clearInterval(timerRef.current);
    setSelected(optIdx);
  }, [selected]);

  // After answer: wait, then proceed
  useEffect(() => {
    if (selected === null) return;

    const isTimeout  = selected === 'timeout';
    const isCorrect  = !isTimeout && q.correctIndices.includes(selected);
    const multiplier = (q.scoreMultiplier || 1) * scoreBonus;
    const pts        = isCorrect ? Math.max(5, Math.round(100 * multiplier * timeLeft / secondsForQ)) : 0;

    const entry = {
      question:       q.question,
      options:        q.options,
      correctIndices: q.correctIndices,
      selectedIndex:  isTimeout ? null : selected,
      correct:        isCorrect,
      score:          pts,
      timeLeft,
      explanation:    q.explanation,
    };

    const delay = isCorrect ? FEEDBACK_DELAY_OK : FEEDBACK_DELAY_ERR;

    const t = setTimeout(() => {
      const newBreakdown   = [...breakdown, entry];
      const newTotalScore  = totalScore + pts;

      if (!isCorrect) {
        clearInterval(totalRef.current);
        onFinish({ breakdown: newBreakdown, totalScore: newTotalScore, totalSecs });
        return;
      }

      // Finestra più corta del numero di domande disponibili (18): prima
      // era 15, quasi quante le domande stesse, quindi dopo una manciata
      // di round la lista "recenti" copriva già tutti i testi possibili e
      // il filtro anti-ripetizione si autoannullava di fatto per l'intera
      // partita — da cui la sensazione di vedere sempre le stesse domande.
      recentTextsRef.current = [q.question, ...recentTextsRef.current].slice(0, 6);
      const next = pickNextQuestion(players, recentTextsRef.current);

      if (!next) {
        // Dataset esaurito: non ci sono più domande generabili, si chiude qui.
        clearInterval(totalRef.current);
        onFinish({ breakdown: newBreakdown, totalScore: newTotalScore, totalSecs });
        return;
      }

      setBreakdown(newBreakdown);
      setTotalScore(newTotalScore);
      setQ(next);
      setIdx(i => i + 1);
      setSelected(null);
    }, delay);

    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const timerPct     = (timeLeft / secondsForQ) * 100;
  const timerWarn    = timeLeft <= 5;
  const isAnswered   = selected !== null;
  const isTimeout    = selected === 'timeout';
  const isCorrect    = !isTimeout && q.correctIndices.includes(selected);
  const levelProgress = idx % DIFFICULTY_STEP;

  const optionState = (optIdx) => {
    if (!isAnswered) return null;
    if (q.correctIndices.includes(optIdx)) return 'correct';
    if (!isTimeout && optIdx === selected) return 'wrong';
    return 'dimmed';
  };

  // Descrizione completa di un'opzione per il feedback: nome, squadra e valore
  const describeOpt = (opt) => {
    if (!opt) return '';
    let s = opt.label;
    if (opt.sub) s += ` (${opt.sub})`;
    if (opt.statValue != null) s += ` — ${opt.statValue}`;
    return s;
  };
  const selectedOpt = isAnswered && !isTimeout ? q.options[selected] : null;

  return (
    <div className="quiz-game">
      {/* Top bar */}
      <div className="quiz-game__top">
        <div className="quiz-game__progress">
          <span>
            Domanda {idx + 1}{level > 0 ? ` · Livello ${level + 1}` : ''}
          </span>
          <div className="quiz-progress-bar">
            <div className="quiz-progress-fill" style={{ width: `${(levelProgress / DIFFICULTY_STEP) * 100}%` }} />
          </div>
        </div>
        <div className="quiz-game__stats">
          <div className="quiz-game__stat">
            <Trophy size={13} />
            <strong>{totalScore} pt</strong>
          </div>
          <div className="quiz-game__stat quiz-game__stat--timer">
            <Timer size={13} />
            <strong>{fmtTime(totalSecs)}</strong>
          </div>
        </div>
      </div>

      {/* Per-question timer */}
      <div className={`quiz-timer-bar ${timerWarn ? 'quiz-timer-bar--warn' : ''}`}>
        <div className="quiz-timer-bar__fill" style={{ width: `${timerPct}%` }} />
      </div>
      <div className={`quiz-timer-countdown ${timerWarn ? 'quiz-timer-countdown--warn' : ''}`}>
        <Clock size={13} /> {timeLeft}s
      </div>

      {/* Question */}
      <div className="quiz-question glass">
        <p className="quiz-question__num">Domanda {idx + 1}</p>
        <h2 className="quiz-question__text">{q.question}</h2>

        {isAnswered && (
          <div className={`quiz-question__feedback ${isCorrect ? 'quiz-question__feedback--ok' : 'quiz-question__feedback--err'}`}>
            {isTimeout ? (
              <div className="quiz-feedback__lines">
                <span><XCircle size={15} /> Tempo scaduto!</span>
                <span>Risposta corretta: <strong>{q.explanation}</strong></span>
              </div>
            ) : isCorrect ? (
              <><CheckCircle2 size={15} /> Corretto! +{Math.max(5, Math.round(100 * (q.scoreMultiplier || 1) * scoreBonus * timeLeft / secondsForQ))} pt — {q.explanation}</>
            ) : (
              <div className="quiz-feedback__lines">
                <span><XCircle size={15} /> Sbagliato. Hai scelto: <strong>{describeOpt(selectedOpt)}</strong></span>
                <span>Risposta corretta: <strong>{q.explanation}</strong></span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Options */}
      <div className="quiz-options">
        {q.options.map((opt, i) => (
          <OptionBtn
            key={i}
            option={opt}
            letter={LETTERS[i]}
            state={optionState(i)}
            onClick={() => handleSelect(i)}
          />
        ))}
      </div>
    </div>
  );
}
